import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RoomMockup } from '../fabric/RoomMockup';
import { ROOM_SCENES } from '../fabric/roomScenes';
import { resolveImageUrl } from '../../lib/imageCache';

const SWATCH = (id) => `/api/storage/files/apollo-shades/fabrics/cdn-original/${id}.jpg`;

// Coulisse Screen Function · Essential 3000 colorways (all offered in 1/3/5/10%)
const TONES = [
  { id: 'ebony', name: 'Ebony', hex: '#2b2a29', texture: SWATCH('e6a4fe74-f7a6-4c82-b0d1-432e012a5c17') },
  { id: 'iron', name: 'Charcoal Iron Grey', hex: '#5d5a56', texture: SWATCH('98cc68ac-eedf-481e-82ed-4edd2a582920') },
  { id: 'chalk', name: 'Chalk', hex: '#e7e3da', texture: SWATCH('a072b0ca-dd55-4720-bc69-5d053c4923ff') },
];

// Comparison-only transmission: slightly wider spread than the configurator's OPENNESS_OPACITY so steps read clearly.
const COMPARE_OPACITY = { 1: 0.99, 3: 0.91, 5: 0.82, 10: 0.64 };

export const OPENNESS_DEFAULTS = {
  title: 'Compare Openness Side by Side',
  intro: 'The same room, window and fabric at each openness. Lower openness gives more privacy and glare control; higher openness gives a clearer view out.',
  caption_1: 'Maximum privacy and glare control.',
  caption_3: 'Strong privacy with some exterior view.',
  caption_5: 'Balanced view, glare and privacy.',
  caption_10: 'Maximum view-through and daylight.',
};
const LEVELS = [{ pct: 1, title: 'Maximum privacy' }, { pct: 3, title: 'Strong privacy' }, { pct: 5, title: 'Balanced' }, { pct: 10, title: 'Maximum view' }];
const SCENE_IDS = ['living', 'office', 'bedroom'];
const LIGHT = { mode: 'screen', label: 'Solar Screen', options: [1, 3, 5, 10] };

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
    className={`px-4 py-2 text-sm border transition-colors ${active ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900'}`}>
    {children}
  </button>
);

const Panel = ({ level, caption, scene, tone }) => (
  <figure className="bg-white flex flex-col" data-testid={`openness-panel-${level.pct}`}>
    <RoomMockup scene={scene} ambience="day" textureUrl={tone.textureUrl} hexColor={tone.hex} light={LIGHT} openness={level.pct} screenOpacity={COMPARE_OPACITY} drop={1} animate={false} />
    <figcaption className="flex-1 px-3 sm:px-4 py-3 sm:py-4 border-x border-b border-neutral-200">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <span className="text-xl sm:text-2xl font-medium text-neutral-900 tabular-nums">{level.pct}%</span>
        <span className="text-[11px] sm:text-xs uppercase tracking-wider text-neutral-500">{level.title}</span>
      </div>
      <p className="mt-1 text-sm text-neutral-600" data-testid={`openness-text-${level.pct}`}>{caption}</p>
    </figcaption>
  </figure>
);

// Selectors + grid; room, fabric and lighting are identical across panels, only the shade transmission changes.
export const OpennessGrid = ({ content = {} }) => {
  const c = { ...OPENNESS_DEFAULTS, ...Object.fromEntries(Object.entries(content).filter(([, v]) => v)) };
  const [sceneId, setSceneId] = useState('living');
  const [toneId, setToneId] = useState('ebony');
  const scene = useMemo(() => ROOM_SCENES.find((s) => s.id === sceneId), [sceneId]);
  const tone = useMemo(() => { const t = TONES.find((x) => x.id === toneId); return { ...t, textureUrl: resolveImageUrl(t.texture, 512) }; }, [toneId]);
  return (
    <>
      <div className="flex flex-wrap gap-x-8 gap-y-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Room">
          {SCENE_IDS.map((id) => <Chip key={id} active={id === sceneId} onClick={() => setSceneId(id)} testId={`openness-scene-${id}`}>{ROOM_SCENES.find((x) => x.id === id).name}</Chip>)}
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Fabric color">
          {TONES.map((t) => (
            <Chip key={t.id} active={t.id === toneId} onClick={() => setToneId(t.id)} testId={`openness-tone-${t.id}`}>
              <span className={`inline-block w-3 h-3 mr-2 align-[-1px] border ${t.id === toneId ? "border-white" : "border-neutral-300"}`} style={{ background: t.hex }} />{t.name}
            </Chip>
          ))}
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {LEVELS.map((l) => <Panel key={l.pct} level={l} caption={c[`caption_${l.pct}`]} scene={scene} tone={tone} />)}
      </div>
      <p className="mt-5 text-xs text-neutral-500 max-w-3xl">Daytime simulation of Coulisse Screen Function, Essential 3000. Darker colors generally give a clearer view out than lighter colors at the same openness. Order samples to compare in your own light.</p>
    </>
  );
};

// Phones: floating chat/WhatsApp buttons step aside while the comparison is on screen (see index.css).
const useHideFloatingWhileVisible = () => {
  const ref = useRef(null);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => document.body.classList.toggle('floating-hidden-mobile', e.isIntersecting), { threshold: 0.01 });
    if (ref.current) io.observe(ref.current);
    return () => { io.disconnect(); document.body.classList.remove('floating-hidden-mobile'); };
  }, []);
  return ref;
};

export const OpennessComparison = ({ content = {} }) => {
  const ref = useHideFloatingWhileVisible();
  return (
  <section ref={ref} id="openness-comparison" className="py-16 md:py-24 bg-neutral-50 scroll-mt-24" data-testid="openness-comparison">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <h2 className="text-neutral-900" data-testid="openness-title">{content.title || OPENNESS_DEFAULTS.title}</h2>
      <p className="mt-4 mb-8 max-w-2xl text-neutral-600" data-testid="openness-intro">{content.intro || OPENNESS_DEFAULTS.intro}</p>
      <OpennessGrid content={content} />
    </div>
  </section>
  );
};

export const OpennessComparisonSection = ({ section }) => <OpennessComparison content={section.props || {}} />;

export default OpennessComparison;
